import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Add, Business } from '@mui/icons-material'
import useTopNavigationContext from 'hooks/useTopNavigationContext/useTopNavigationContext'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useDeletePartnership, useInfinitePartnerships } from '../api'
import useDebouncedValue from 'hooks/useDebouncedValue'
import { Partnership } from 'common/types/kraken-core/Partnership'
import PartnershipDetailComponent from './PartnershipDetail'
import { useHistory, useParams } from 'react-router-dom'
import ActiveStatusTag from 'atoms/ActiveStatusTag/ActiveStatusTag'
import { useTabTitle } from 'hooks/useTabTitle/useTabTitle'
import { useInfiniteScroll } from 'hooks/useInfiniteScroll'
import mapping from 'atoms/Icon/mapping'

const PartnershipList = () => {
  useTabTitle('Partnerships')
  const history = useHistory()
  const { id } = useParams<any>()
  const [searchFilter, setSearchFilter] = useState<string>('')
  const [selectedPartnership, setSelectedPartnership] = useState<Partial<Partnership>>()
  const [selectedToDelete, setSelectedToDelete] = useState<Partnership>()

  const debouncedSearchFilter = useDebouncedValue<string>(searchFilter, 500)

  const {
    partnerships: rawPartnerships,
    loadMore,
    isLoading,
    error,
    hasMore,
    reset,
  } = useInfinitePartnerships({
    search: debouncedSearchFilter,
  })

  // Use tags from the API response directly
  const partnerships = rawPartnerships

  const {
    mutate: deletePartnership,
    error: deleteError,
    isSuccess: isDeleteSuccess,
    isLoading: isDeleting,
  } = useDeletePartnership()

  const topNavigationContext = useTopNavigationContext()

  // Infinite scroll setup
  const sentinelRef = useInfiniteScroll(loadMore, {
    enabled: hasMore && !isLoading,
  })

  const handleEdit = (partnership: Partnership) => {
    setSelectedPartnership(partnership)
    history.push(`partnership/${partnership.id}`)
  }

  const handleCreateNew = () => {
    setSelectedPartnership({})
  }

  const handleCloseDetail = () => {
    setSelectedPartnership(undefined)
    history.replace('/partnership')
    // Removed reset() call to prevent unnecessary reloading when closing drawer
  }

  const handleOnPickupToDelete = (partnership: Partnership) => {
    setSelectedToDelete(partnership)
  }

  const handleDelete = () => {
    if (selectedToDelete && selectedToDelete.id) deletePartnership(selectedToDelete?.id)
  }

  const getPartnershipRep = (partnership: Partnership) => {
    return `${partnership.party.name} - ${partnership.counterparty.name}`
  }

  useEffect(() => {
    topNavigationContext.updateState({
      children: (
        <Box className="w-full" data-testid="header" display="flex" alignItems="center" gap={1}>
          {mapping['Partnership']}
          <Typography variant="h2" fontWeight="bold">
            Partnerships
          </Typography>
        </Box>
      ),
    })
  }, [])

  useEffect(() => {
    if (error) {
      toast.error(`Something went wrong when loading: ${error}`)
      return
    }
  }, [error])

  useEffect(() => {
    if (deleteError) {
      toast.error(`Error when deleting: ${deleteError}`)
    } else if (isDeleteSuccess) {
      toast.success('Successfully deleted')
      setSelectedToDelete(undefined)
      reset()
    }
  }, [deleteError, isDeleteSuccess, reset])

  useEffect(() => {
    if (id) {
      setSelectedPartnership({ id })
    }
  }, [id])

  // Reset and load initial data when filters change
  useEffect(() => {
    reset()
    loadMore()
  }, [debouncedSearchFilter])

  return (
    <>
      <Dialog
        open={!!selectedToDelete}
        onClose={() => setSelectedToDelete(undefined)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Partnership</DialogTitle>
        {selectedToDelete?.id ? (
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this partnership{' '}
              <Typography component="span" fontWeight="bold">
                {getPartnershipRep(selectedToDelete)}
              </Typography>
              ?
            </DialogContentText>
          </DialogContent>
        ) : null}
        <DialogActions>
          <Button onClick={() => setSelectedToDelete(undefined)}>Cancel</Button>
          <Button onClick={handleDelete} disabled={isDeleting} color="error" variant="contained">
            {isDeleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <PartnershipDetailComponent
        open={selectedPartnership ? true : false}
        onClose={handleCloseDetail}
        id={selectedPartnership?.id}
      />

      <Stack
        spacing={2}
        margin={1}
        sx={{
          height: '90vh',
          overflow: 'hidden',
        }}
      >
        {/* Header and Controls */}
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <TextField
            label="Search partnerships..."
            value={searchFilter}
            onChange={e => setSearchFilter(e.target.value)}
            sx={{
              mt: 1,
              width: '100%',
              // maxWidth: '400px',
            }}
          />
          <Button variant="contained" startIcon={<Add />} onClick={handleCreateNew} size="large">
            New
          </Button>
        </Box>

        <Stack
          spacing={2}
          sx={{
            flex: 1,
            overflowY: 'auto',
          }}
        >
          {/* Card Grid */}
          <Grid container spacing={2}>
            {partnerships.map(partnership => (
              <Grid key={partnership.id} item xs={12} sm={6} md={4} lg={3}>
                <CardActionArea
                  onClick={() => handleEdit(partnership)}
                  sx={{
                    borderRadius: '20px',
                  }}
                >
                  <Card
                    sx={{
                      height: '200px', // Set a consistent height for all cards
                      borderRadius: '20px',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        height: '100%', // Ensure the Box spans the full height of the Card
                        padding: 0,
                      }}
                    >
                      <CardHeader
                        title={
                          <Typography
                            variant="h4"
                            fontWeight="bold"
                            sx={{
                              display: '-webkit-box',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 2, // Limit to 2 lines
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxHeight: '3em', // Ensure the title doesn't stretch
                            }}
                          >
                            {partnership.party.name} • {partnership.counterparty.name}
                          </Typography>
                        }
                        avatar={
                          <Avatar
                            sizes="large"
                            sx={{
                              backgroundColor: 'secondary.main',
                            }}
                          >
                            <Business />
                          </Avatar>
                        }
                        action={<ActiveStatusTag active={partnership.is_active} />}
                      />
                      <CardContent>
                        <Stack spacing={1}>
                          {/* Tags */}
                          {partnership.tags && partnership.tags.length > 0 && (
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {partnership.tags.map((tag, index) => (
                                <Chip
                                  key={index}
                                  label={tag}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontWeight: 500,
                                  }}
                                />
                              ))}
                            </Box>
                          )}

                          {/* Feature tags from groups */}
                          {partnership.groups && partnership.groups.length > 0 && (
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {partnership.groups.map(group => (
                                <Chip
                                  key={group.id}
                                  label={group.name}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    backgroundColor: '#FFF9C4',
                                    color: '#333',
                                    fontWeight: 500,
                                  }}
                                />
                              ))}
                            </Box>
                          )}

                          {/* Installation and Message counts */}
                          <Box display="flex" gap={2} color="text.secondary">
                            <Typography variant="body2">
                              {partnership.installations_count || 0} installations
                            </Typography>
                            {/* <Typography variant="body2">
                              {partnership.message_count || 0} messages
                            </Typography> */}
                          </Box>
                        </Stack>
                      </CardContent>
                    </Box>
                  </Card>
                </CardActionArea>
              </Grid>
            ))}
          </Grid>

          {/* Loading indicator and sentinel for infinite scroll */}
          {isLoading && (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          )}

          {/* Sentinel element for infinite scroll */}
          <Box ref={sentinelRef} sx={{ height: '20px' }} />

          {/* No results message */}
          {!isLoading && partnerships.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary">
                No partnerships available.
              </Typography>
            </Box>
          )}
        </Stack>
      </Stack>
    </>
  )
}

export default PartnershipList
